// File: webelieve-frontend/src/features/admin/components/settings/workflow-builder/workflowApi.ts
// EF-7E: API client for intake workflow CRUD

import { apiClient } from '@/config/api';

// --- Types ---

export type WorkflowTriggerType = 'stage_change' | 'form_submission' | 'time_elapsed' | 'document_uploaded';
export type WorkflowActionType = 'send_email' | 'create_task' | 'prepare_document' | 'set_reminder' | 'assign_attorney' | 'update_stage';

export interface WorkflowCondition {
  field: string;
  operator: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'in';
  value: any;
}

export interface WorkflowAction {
  action_id: string;
  action_type: WorkflowActionType;
  label: string;
  config: Record<string, any>;
  conditions?: WorkflowCondition[];
  order: number;
}

export interface WorkflowTriggerConfig {
  trigger_type: WorkflowTriggerType;
  from_stage?: string;
  to_stage?: string;
  form_id?: string;
  days_elapsed?: number;
  stage_name?: string;
}

export interface IntakeWorkflow {
  workflow_id: string;
  realm_id: string;
  name: string;
  description?: string;
  trigger_type: WorkflowTriggerType;
  trigger_config: WorkflowTriggerConfig;
  actions: WorkflowAction[];
  is_active: boolean;
  max_actions_per_run: number;
  max_emails_per_lead_per_day: number;
  total_executions: number;
  last_executed_at?: string;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateWorkflowData {
  name: string;
  description?: string;
  trigger_type: WorkflowTriggerType;
  trigger_config: WorkflowTriggerConfig;
  actions: WorkflowAction[];
  is_active?: boolean;
  max_actions_per_run?: number;
  max_emails_per_lead_per_day?: number;
}

export interface WorkflowAnalytics {
  total_executions: number;
  successful: number;
  failed: number;
  circuit_broken: number;
  avg_actions_per_run: number;
  last_executed_at: string | null;
}

// --- API Client ---

const BASE = '/api/v1/admin/intake-workflows';

export const workflowApi = {
  listWorkflows: async (): Promise<IntakeWorkflow[]> => {
    const res = await apiClient.get<{ data: { workflows: IntakeWorkflow[]; total: number } }>(BASE);
    if (!(res as any).success) throw new Error((res as any).error || 'Failed to fetch workflows');
    return (res as any).data?.workflows || [];
  },

  getWorkflow: async (workflowId: string): Promise<IntakeWorkflow> => {
    const res = await apiClient.get<{ data: IntakeWorkflow }>(`${BASE}/${workflowId}`);
    if (!(res as any).success) throw new Error((res as any).error || 'Failed to fetch workflow');
    return (res as any).data;
  },

  createWorkflow: async (data: CreateWorkflowData): Promise<IntakeWorkflow> => {
    const res = await apiClient.post<{ data: IntakeWorkflow }>(BASE, data);
    if (!(res as any).success) throw new Error((res as any).error || 'Failed to create workflow');
    return (res as any).data;
  },

  updateWorkflow: async (workflowId: string, data: Partial<CreateWorkflowData>): Promise<IntakeWorkflow> => {
    const res = await apiClient.put<{ data: IntakeWorkflow }>(`${BASE}/${workflowId}`, data);
    if (!(res as any).success) throw new Error((res as any).error || 'Failed to update workflow');
    return (res as any).data;
  },

  deleteWorkflow: async (workflowId: string): Promise<void> => {
    const res = await apiClient.delete<void>(`${BASE}/${workflowId}`);
    if (!(res as any).success) throw new Error((res as any).error || 'Failed to delete workflow');
  },

  duplicateWorkflow: async (workflowId: string, newName?: string): Promise<IntakeWorkflow> => {
    const res = await apiClient.post<{ data: IntakeWorkflow }>(`${BASE}/${workflowId}/duplicate`, { new_name: newName });
    if (!(res as any).success) throw new Error((res as any).error || 'Failed to duplicate workflow');
    return (res as any).data;
  },

  toggleActive: async (workflowId: string): Promise<IntakeWorkflow> => {
    const res = await apiClient.patch<{ data: IntakeWorkflow }>(`${BASE}/${workflowId}/toggle`);
    if (!(res as any).success) throw new Error((res as any).error || 'Failed to toggle workflow');
    return (res as any).data;
  },

  getAnalytics: async (workflowId: string): Promise<WorkflowAnalytics> => {
    const res = await apiClient.get<{ data: WorkflowAnalytics }>(`${BASE}/${workflowId}/analytics`);
    if (!(res as any).success) throw new Error((res as any).error || 'Failed to fetch analytics');
    return (res as any).data;
  },
};
